/**
 * Product inquiry ("Produktanfrage") logic.
 *
 * Lets customers collect sold-out products across the store and send them
 * in a single message via the theme's product inquiry drawer + Shopify's
 * native contact form.
 *
 * Items are persisted in localStorage under 'productInquiryItems' so the
 * list survives navigation between product pages.
 */

const STORAGE_KEY = 'productInquiryItems';

function getItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
}

function setItems(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    /* localStorage unavailable — fail silently */
  }
}

function clearItems() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    /* localStorage unavailable — fail silently */
  }
}

function addItem(item) {
  const items = getItems();
  if (!items.some((existing) => existing.id === item.id)) {
    items.push(item);
    setItems(items);
  }
  renderItems();
  openDrawer();
}

function removeItem(id) {
  const items = getItems().filter((item) => String(item.id) !== String(id));
  setItems(items);
  renderItems();
}

function getDialog() {
  const drawer = document.getElementById('product-inquiry-drawer');
  if (!drawer) return null;
  return drawer.querySelector('dialog');
}

function openDrawer() {
  const dialog = getDialog();
  if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
    dialog.showModal();
  }
}

function closeDrawer() {
  const dialog = getDialog();
  if (dialog && typeof dialog.close === 'function' && dialog.open) {
    dialog.close();
  }
}

function resetForm() {
  const form = document.getElementById('ProductInquiryForm');
  if (form && typeof form.reset === 'function') {
    form.reset();
  }
}

function discardAndClose() {
  clearItems();
  resetForm();
  renderItems();
  closeDrawer();
}

function renderItems() {
  const container = document.querySelector('[data-product-inquiry-items]');
  const emptyState = document.querySelector('[data-product-inquiry-empty]');
  const submitButton = document.querySelector('[data-product-inquiry-submit]');
  if (!container) return;

  const items = getItems();
  container.innerHTML = '';

  if (items.length === 0) {
    if (emptyState) emptyState.hidden = false;
    if (submitButton) submitButton.disabled = true;
    return;
  }

  if (emptyState) emptyState.hidden = true;
  if (submitButton) submitButton.disabled = false;

  items.forEach((item) => {
    const row = document.createElement('div');
    row.className = 'product-inquiry__item';

    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      row.appendChild(img);
    }

    const title = document.createElement('span');
    title.className = 'product-inquiry__item-title';
    title.textContent = item.title;
    row.appendChild(title);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'product-inquiry__item-remove';
    removeButton.setAttribute('aria-label', 'Entfernen: ' + item.title);
    removeButton.textContent = '×';
    removeButton.addEventListener('click', () => removeItem(item.id));
    row.appendChild(removeButton);

    container.appendChild(row);
  });
}

function syncFormBody() {
  const bodyInput = document.querySelector('[data-product-inquiry-body]');
  const messageInput = document.querySelector('[data-product-inquiry-message]');
  if (!bodyInput) return;

  const items = getItems();
  const lines = items.map((item) => '- ' + item.title + ' (' + item.url + ')');
  const productBlock = lines.length
    ? 'Produktanfrage für folgende Artikel:\n' + lines.join('\n')
    : 'Produktanfrage';

  const message = messageInput ? messageInput.value.trim() : '';
  bodyInput.value = message ? productBlock + '\n\nNachricht:\n' + message : productBlock;
}

function init() {
  renderItems();

  document.addEventListener('click', (event) => {
    const addTrigger = event.target.closest('[data-product-inquiry-add]');
    if (addTrigger) {
      event.preventDefault();
      addItem({
        id: addTrigger.getAttribute('data-product-id'),
        title: addTrigger.getAttribute('data-product-title'),
        url: addTrigger.getAttribute('data-product-url'),
        image: addTrigger.getAttribute('data-product-image'),
      });
      return;
    }

    const closeTrigger = event.target.closest('[data-product-inquiry-close]');
    if (closeTrigger) {
      event.preventDefault();
      discardAndClose();
    }
  });

  const form = document.getElementById('ProductInquiryForm');
  if (form) {
    form.addEventListener('submit', () => {
      syncFormBody();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
